import React, { useState, useEffect, useCallback } from "react";
import { Formik } from "formik";
import { isEmpty } from "lodash";
import { FormattedMessage } from "react-intl";
import { strip, format, parse } from "@origo_vl_banking/utils";
import * as Yup from 'yup';
import { Label } from "../../../components/grid/styled-components";
import { normalizeMortgageItem, preventNumError } from "../../../utils/helpers";
import TextInput from "../../../components/text-input/TextInput";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../../components/button";
import { validate } from "../../../utils/validate";
import {
  calcConsolidation,
  setAdditionalDeposit,
  setCollateralRedemption,
  setCollateralRedemptionAndClearLicensePlateChanged,
  setCollateralValue,
  setCurrentCollateralAmount,
  setCurrentCollateralItem,
  setInitialDeposit,
  getCurrentCarInfo,
  setDepositPercentage,
  clearAllApplicantData,
} from "../actions";
import {
  CurrentCollateralGrid,
  InputWrapper,
  BtnBottomLeftWrapper,
  VehicleWrapper,
  VehicleType
} from "./styled-components";
import PropTypes from "prop-types";

// Form validation rules
const validators = {
  purchasePrice: Yup.number(),
  collateralItem: validate.formik.required,
  collateralRedemption: Yup.number(),
  collateralValue: Yup.number()
};

const validationSchema = Yup.object().shape(validators);

const CurrentCollateral = ({ toggleRmbalModal }) => {
  const dispatch = useDispatch();
  const { validationError } = useSelector((state) => state.app);
  const [isLoadingCarInfo, setIsLoadingCarInfo] = useState(false);
  const [localDepositPercentage, setLocalDepositPercentage] = useState('');
  const [licensePlateChanged, setLicensePlateChanged] = useState(false);
  
  // Get relevant state from Redux store
  const consolidation = useSelector((state) => state.consolidation);
  const { 
    newCollateral,
    newCollateralNumber,
    currentCollateral,
    currentCollateralNumber,
    collateralRedemption,
    collateralValue,
    additionalCashDeposit,
    initialDeposit,
    depositPercentage,
    hasFetchedConsolidationRmbal,
  } = consolidation;

  // Sync local state with Redux state
  useEffect(() => {
    setLocalDepositPercentage(depositPercentage ? format.decimal(depositPercentage) : '');
  }, [depositPercentage]);

  /**
   * Updates total deposit and deposit percentage based on ownership balance and additional deposit
   */
  const updateTotalDepositAndPercentage = useCallback((ownershipBalance, additionalAmount) => {
    if (!newCollateral?.salesAmt) return;
    
    // Allow both ownership value and additional deposit to be negative
    const ownershipValue = ownershipBalance || 0;
    const additionalValue = additionalAmount || 0;
    const calculatedTotal = ownershipValue + additionalValue;
    
    // Ensure total deposit is never negative for display and calculations
    const totalDeposit = Math.max(0, calculatedTotal);
    dispatch(setInitialDeposit(totalDeposit));
    
    // Only calculate percentage if new collateral sales amount is positive
    if (newCollateral.salesAmt > 0) {
      // Calculate percentage based on non-negative total deposit
      const newPercentage = parseFloat(((totalDeposit / newCollateral.salesAmt) * 100).toFixed(2));
      dispatch(setDepositPercentage(newPercentage));
    }
    
    // Trigger calculation to update loan details
    dispatch(calcConsolidation());
  }, [newCollateral?.salesAmt, dispatch]);

  /**
   * Initialize deposit values when collateral info is first loaded
   */
  useEffect(() => {
    if (newCollateral?.salesAmt > 0) {
      const minPercentage = 0;
      
      // If we don't have deposit values yet, initialize them
      if ((!initialDeposit || initialDeposit === 0) && (!additionalCashDeposit || additionalCashDeposit === 0)) {
        const ownershipBalance = collateralValue || 0;
        
        // When first initializing, check the ownership balance
        if (ownershipBalance !== 0) {
          // If we have ownership balance, use it for initial deposit
          // For negative ownership, we'll need additional deposit to compensate
          const additionalNeeded = Math.max(0, -ownershipBalance);
          
          dispatch(setInitialDeposit(Math.max(0, ownershipBalance + additionalNeeded)));
          dispatch(setAdditionalDeposit(additionalNeeded));
          
          // Calculate percentage based on the total with 2 decimal places
          const totalDeposit = Math.max(0, ownershipBalance + additionalNeeded);
          const currentPercentage = parseFloat(((totalDeposit / newCollateral.salesAmt) * 100).toFixed(2));
          dispatch(setDepositPercentage(currentPercentage));
        } else {
          // If ownership balance is 0, set minimum values
          const minDepositAmount = Math.round(newCollateral.salesAmt * (minPercentage / 100));
          dispatch(setInitialDeposit(minDepositAmount));
          dispatch(setAdditionalDeposit(minDepositAmount));
          dispatch(setDepositPercentage(minPercentage));
        }
      } 
      // If we have initialDeposit but depositPercent isn't set, update it
      else if (initialDeposit > 0 && (!depositPercentage || depositPercentage === 0)) {
        const currentPercentage = parseFloat(((initialDeposit / newCollateral.salesAmt) * 100).toFixed(2));
        dispatch(setDepositPercentage(Math.max(currentPercentage, minPercentage)));
      }
    }
  }, [newCollateral?.salesAmt, initialDeposit, additionalCashDeposit, collateralValue, depositPercentage, dispatch]);

  /**
   * Calculate ownership balance when purchase price or remaining loan balance changes
   */
  useEffect(() => {
    if (currentCollateral?.salesAmt) {
      // Calculate ownership balance - allow it to be negative
      const redemptionAmount = collateralRedemption || 0;
      const ownershipBalance = currentCollateral.salesAmt - redemptionAmount;
      
      // Only update if value actually changed to prevent loops
      if (ownershipBalance !== collateralValue) {
        dispatch(setCollateralValue(ownershipBalance));
        
        if (newCollateral?.salesAmt) {
          // Don't try to maintain percentage - just use the current additional deposit
          const currentAdditional = additionalCashDeposit || 0;
          
          // Calculate the new total deposit
          const totalDeposit = Math.max(0, ownershipBalance + currentAdditional);
          dispatch(setInitialDeposit(totalDeposit));
          
          // Recalculate percentage based on new total with 2 decimal places
          const newPercentage = parseFloat(((totalDeposit / newCollateral.salesAmt) * 100).toFixed(2));
          dispatch(setDepositPercentage(newPercentage));
          
          // Trigger calculation to update loan details
          dispatch(calcConsolidation());
        }
      }    
    }
  }, [currentCollateral?.salesAmt, collateralRedemption, collateralValue, newCollateral?.salesAmt, additionalCashDeposit, licensePlateChanged, dispatch]);

  /**
   * Handle changes to the cash deposit percentage field
   */
  const handleCashDepositPercentOnBlur = (value, handleBlur, e) => {
    handleBlur(e);
    
    // Normalize dots to commas for parsing compatibility
    const normalizedValue = value.replace('.', ',');
    if (!value || isNaN(parse.decimal(normalizedValue))) return;
    
    const inputPercentage = parseFloat(parse.decimal(normalizedValue).toFixed(2));
    
    // Only proceed if the value changed when rounded to 2 decimal places
    const currentPercentage = parseFloat((depositPercentage || 0).toFixed(2));
    if (inputPercentage === currentPercentage) return;
    
    if (!newCollateral?.salesAmt) return;

    const ownershipBalance = collateralValue || 0;
    
    // If user enters negative percentage, set it to 0
    const targetPercentage = inputPercentage < 0 ? 0 : inputPercentage;
    
    // Calculate target total deposit based on percentage
    const targetTotalDeposit = Math.round(newCollateral.salesAmt * (targetPercentage / 100));
    
    // Calculate what additional deposit would be needed (can be negative)
    const neededAdditional = targetTotalDeposit - ownershipBalance;
    
    // Update state values - allow negative additional deposit
    dispatch(setAdditionalDeposit(neededAdditional));
    
    // Ensure total deposit is never negative
    const actualTotalDeposit = Math.max(0, ownershipBalance + neededAdditional);
    dispatch(setInitialDeposit(actualTotalDeposit));
    
    // Calculate actual percentage based on final total deposit
    const actualPercentage = actualTotalDeposit > 0 ? 
      parseFloat(((actualTotalDeposit / newCollateral.salesAmt) * 100).toFixed(2)) : 0;
    
    // Always update Redux to ensure consistency
    dispatch(setDepositPercentage(actualPercentage));
    // Update the input field - if user entered negative, show 0, otherwise show actual percentage
    const displayPercentage = inputPercentage < 0 ? 0 : actualPercentage;
    setLocalDepositPercentage(format.decimal(displayPercentage));
    
    // Calculate after all values are set
    setTimeout(() => dispatch(calcConsolidation()), 0);
  };

  /**
   * Handle changes to the collateral item (license plate) field
   */
  const handleCollateralItem = async (value, handleBlur, e, setFieldTouched) => {
    handleBlur(e);
    if (setFieldTouched) {
      setFieldTouched('collateralItem', true, false);
    }
        
    if (!isEmpty(value) && value !== currentCollateralNumber) {
      setIsLoadingCarInfo(true);
      try {
        dispatch(clearAllApplicantData());
        dispatch(setCurrentCollateralItem(value));
        dispatch(getCurrentCarInfo(value));
        dispatch(setCollateralRedemptionAndClearLicensePlateChanged());
        setLicensePlateChanged(prev => !prev);
      } catch (error) {
        console.error('Failed to fetch car info:', error);
      } finally {
        setIsLoadingCarInfo(false);
      }
    }
  };

  /**
   * Handle changes to the remaining loan balance
   */
  const handleRemainingLoanBalance = (value, handleBlur, e) => {
    handleBlur(e);
    if (isEmpty(value)) return;
    
    const amount = parseInt(strip.amount(value), 10);
    if (isNaN(amount)) return;
    
    if (currentCollateral?.salesAmt && amount > currentCollateral.salesAmt) {
      return;
    }
    
    dispatch(setCollateralRedemption(amount));
  };

  /**
   * Handle changes to the extra deposit field
   */
  const handleExtraDepositOnBlur = (value, handleBlur, e) => {
    handleBlur(e);
    
    // Check if the value is empty
    if (isEmpty(value)) {
      if (value == additionalCashDeposit) return;

      // If value is empty, set to 0
      dispatch(setAdditionalDeposit(0));
      const ownershipBalance = collateralValue || 0;
      updateTotalDepositAndPercentage(ownershipBalance, 0);
      return;
    }
    
    // Parse and validate the amount
    const amount = parseInt(strip.amount(value), 10);
    if (isNaN(amount)) return;
    
    // Check if the value hasn't changed
    if (amount === additionalCashDeposit) return;
    
    // Allow negative additional deposits
    dispatch(setAdditionalDeposit(amount));
    
    // Calculate and update the total deposit and percentage
    const ownershipBalance = collateralValue || 0;
    updateTotalDepositAndPercentage(ownershipBalance, amount);
  };

  /**
   * Handle changes to the purchase price
   */
  const handlePurchasePriceChange = (e, handleChange, setFieldValue) => {
    preventNumError(e, handleChange);
    const cleanValue = e.target.value.replace(/\./g, '');
    const numValue = Number(cleanValue);
    
    if (!isNaN(numValue) && numValue >= 0) {
      setFieldValue('purchasePrice', numValue);
    }
  };

  /**
   * Handle blur event for purchase price
   */
  const handleBlurPrice = (value, handleBlur, e) => {
    handleBlur(e);
    if (isEmpty(value)) return;
    
    const price = parseInt(strip.amount(value), 10);
    if (price < 0 || price === currentCollateral?.salesAmt) return;
    
    // Update current collateral amount in Redux
    dispatch(setCurrentCollateralAmount(price));
    
    // Calculate new ownership balance based on updated purchase price
    // Allow ownership balance to be negative
    const redemptionAmount = collateralRedemption || 0;
    const newOwnershipBalance = price - redemptionAmount;
    
    // Only update if value actually changed to prevent loops
    if (newOwnershipBalance !== collateralValue) {
      // Update the ownership balance
      dispatch(setCollateralValue(newOwnershipBalance));
      
      // Update additional deposit and total deposit based on new ownership balance
      if (newCollateral?.salesAmt) {
        // Don't try to maintain percentage - just use the current additional deposit
        const currentAdditional = additionalCashDeposit || 0;
        
        // Calculate new total deposit - ensure it's not negative
        const totalDeposit = Math.max(0, newOwnershipBalance + currentAdditional);
        dispatch(setInitialDeposit(totalDeposit));
        
        // Recalculate percentage based on new total with 2 decimal places
        const newPercentage = parseFloat(((totalDeposit / newCollateral.salesAmt) * 100).toFixed(2));
        dispatch(setDepositPercentage(newPercentage));
      } else {
        // If we don't have new collateral info yet, just update initial deposit
        const currentAdditional = additionalCashDeposit || 0;
        const totalDeposit = Math.max(0, newOwnershipBalance + currentAdditional);
        dispatch(setInitialDeposit(totalDeposit));
      }
      
      // Trigger calculation to update loan details
      dispatch(calcConsolidation());
    }
  };

  /**
   * Renders the price difference between new and current collateral
   */
  const renderPriceDifference = () => {
    if (!newCollateral?.salesAmt || !currentCollateral?.salesAmt) return null;
    
    return (
      <div>
        <p style={{ marginTop: 0 }}>
          <Label>
            <FormattedMessage id="Consolidation.Difference" />
          </Label>
        </p>
        <span>
          {format.amount(newCollateral.salesAmt - currentCollateral.salesAmt)} kr.
        </span>
      </div>
    );
  };

  /**
   * Check if Get Status button should be disabled
   */
  const isGetStatusDisabled = !(
    newCollateralNumber &&
    newCollateral?.salesAmt &&
    currentCollateralNumber &&
    currentCollateral?.salesAmt &&
    consolidation.editable
  );

  /**
   * Get initial form values from Redux store
   */
  const getInitialValues = () => ({
    purchasePrice: currentCollateral?.salesAmt || "",
    collateralItem: currentCollateralNumber || "",
    collateralRedemption: collateralRedemption || "",
    collateralValue: collateralValue || "",
    additionalCashDeposit: additionalCashDeposit || "",
    initialDeposit: initialDeposit || "",
  });

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={getInitialValues()}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
    >
      {({
        handleBlur,
        handleChange,
        setFieldValue,
        setFieldTouched,
        values,
        errors,
        touched,
      }) => (
        <CurrentCollateralGrid>
          <InputWrapper gridArea="purchasePrice">
            <TextInput
              label="Consolidation.SellingPrice"
              normalize="amount"
              id="purchasePrice"
              name="purchasePrice"
              height="34px"
              align="right"
              star="red"
              value={values.purchasePrice}
              onBlur={(e) => handleBlurPrice(e.target.value, handleBlur, e)}
              onChange={(e) => handlePurchasePriceChange(e, handleChange, setFieldValue)}
              tabIndex="2"
              rightSideText="kr."
              disabled={!consolidation.editable}
              error={(touched.purchasePrice && errors.purchasePrice) || (!touched.purchasePrice && validationError && !values.purchasePrice)}
            />
          </InputWrapper>

          <InputWrapper gridArea="priceDifference">
            {renderPriceDifference()}
          </InputWrapper>

          <InputWrapper gridArea="licensePlate">
            <TextInput
              label="Mortgage.Item"
              name="collateralItem"
              height="34px"
              maxLength={6}
              star="red"
              value={values.collateralItem?.toUpperCase() || ''}
              onBlur={(e) => handleCollateralItem(e.target.value, handleBlur, e, setFieldTouched)}
              onChange={(e) => normalizeMortgageItem(e.target.value, setFieldValue, "collateralItem")}
              align="right"
              tabIndex="2.1"
              disabled={!consolidation.editable}
              error={(touched.collateralItem && errors.collateralItem) || (!touched.collateralItem && validationError && !values.collateralItem)}
            />
          </InputWrapper>

          <InputWrapper gridArea="collateralItem">
            <VehicleWrapper>
              <Label>
                <FormattedMessage id="Mortgage.Vehicle" />
              </Label>
              <VehicleType>
                {isLoadingCarInfo ? 'Loading...' : currentCollateral?.name ?? ""}
              </VehicleType>
            </VehicleWrapper>
          </InputWrapper>

          <InputWrapper gridArea="remainingLoanBalance">
            <TextInput
              label="Consolidation.RemainingLoanBalance"
              normalize="amount"
              name="remainingLoanBalance"
              height="34px"
              align="right"
              value={format.amount(values.collateralRedemption)}
              onChange={(e) => {
                preventNumError(e, handleChange);
                setFieldValue('collateralRedemption', e.target.value, false);
              }}
              onBlur={(e) => handleRemainingLoanBalance(e.target.value, handleBlur, e)}
              tabIndex="2.2"
              rightSideText="kr."
              disabled={!consolidation.editable}
            />
          </InputWrapper>

          <InputWrapper gridArea="ownershipBalance">
            <TextInput
              label="Consolidation.OwnershipBalance"
              name="ownershipBalance"
              height="34px"
              align="right"
              value={format.amount(values.collateralValue)}
              disabled
              tabIndex="20"
              rightSideText="kr."
            />
          </InputWrapper>

          <InputWrapper gridArea="extraDeposit">
            <TextInput
              label="Consolidation.ExtraDeposit"
              name="extraDeposit"
              height="34px"
              align="right"
              value={format.amount(values.additionalCashDeposit)}
              disabled={!consolidation.editable || !newCollateral?.salesAmt}
              onChange={(e) => {
                const value = e.target.value;
                // Allow negative numbers but prevent invalid characters
                // Allow digits, dots, commas, and a single minus sign at the start
                if (value === '' || /^-?\d*[.,]?\d*$/.test(value.replace(/\./g, ''))) {
                  preventNumError(e, handleChange);
                  setFieldValue('additionalCashDeposit', e.target.value, false);
                }
              }}
              onBlur={(e) => handleExtraDepositOnBlur(e.target.value, handleBlur, e)}
              tabIndex="2.3"
              rightSideText="kr."
            />
          </InputWrapper>

          <InputWrapper gridArea="totalDeposit">
            <TextInput
              label="Consolidation.TotalDeposit"
              normalize="amount"
              name="totalDeposit"
              height="34px"
              align="right"
              value={values.initialDeposit}
              disabled
              tabIndex="2.4"
              rightSideText="kr."
            />
          </InputWrapper>

          <InputWrapper gridArea="totalDepositPercentage">
            <TextInput
              label="Consolidation.DepositPercentage"
              height="34px"
              name="totalDepositPercentage"
              onBlur={(e) => handleCashDepositPercentOnBlur(e.target.value, handleBlur, e)}
              onChange={(e) => {
                const value = e.target.value;
                // Allow values with up to 2 decimal places (e.g., 10.25 or 10,25)
                if (value === '' || /^-?\d*[,.]?\d{0,2}$/.test(value)) {
                  setLocalDepositPercentage(value);
                }
              }}
              value={localDepositPercentage}
              rightSideText="%"
              align="right"
              disabled={!consolidation.editable || !newCollateral?.salesAmt}
              tabIndex="2.5"
            />
          </InputWrapper>

          <InputWrapper gridArea="getStatusBtn">
            <BtnBottomLeftWrapper>
              <Button
                disabled={isGetStatusDisabled}
                border="1px solid transparent"
                background={isGetStatusDisabled ? "#ccc" : "hsl(46, 98%, 57%)"}
                color={isGetStatusDisabled ? "#fff" : "#333"}
                onClick={() => toggleRmbalModal(true)}
                tabIndex="2.6"
                content={
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span>
                      <FormattedMessage id="Consolidation.GetStatus" />
                    </span>
                  </div>
                }
              />
            </BtnBottomLeftWrapper>
          </InputWrapper>
        </CurrentCollateralGrid>
      )}
    </Formik>
  );
};

CurrentCollateral.propTypes = {
  toggleRmbalModal: PropTypes.func.isRequired,
};

export default CurrentCollateral;
